import { useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { type ConsentPreferences } from "@/hooks/useCookieConsent";
import CustomButton from "@/shared/customFields/CustomButton";

interface Props {
  open: boolean;
  current: ConsentPreferences | null;
  onSave: (prefs: ConsentPreferences) => void;
  onAcceptAll: () => void;
  onClose: () => void;
}

export default function CookieManageModal({ open, current, onSave, onAcceptAll, onClose }: Props) {
  const { t } = useTranslation("cookieConsent");

  const [analytics, setAnalytics] = useState(current?.analytics ?? true);
  const [errorMonitoring, setErrorMonitoring] = useState(current?.errorMonitoring ?? true);

  function handleSave() {
    onSave({ analytics, errorMonitoring });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-sm p-0 gap-0 overflow-hidden">
        <DialogHeader className="flex flex-row items-center justify-between px-5 py-4 border-b">
          <DialogTitle className="text-base font-semibold">{t("manage.title")}</DialogTitle>
          <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label={t("manage.close")}>
            <X className="w-4 h-4" />
          </Button>
        </DialogHeader>

        <div className="divide-y divide-border">
          <CookieRow
            title={t("manage.essential.title")}
            description={t("manage.essential.description")}
            badge={t("manage.alwaysActive")}
            checked
            disabled
          />
          <CookieRow
            title={t("manage.analytics.title")}
            description={t("manage.analytics.description")}
            checked={analytics}
            onChange={setAnalytics}
          />
          <CookieRow
            title={t("manage.errorMonitoring.title")}
            description={t("manage.errorMonitoring.description")}
            checked={errorMonitoring}
            onChange={setErrorMonitoring}
          />
          <CookieRow
            title={t("manage.serviceData.title")}
            description={t("manage.serviceData.description")}
            badge={t("manage.serviceData.badge")}
            checked
            disabled
          />
        </div>

        <DialogFooter className="px-5 py-3 border-t flex-row gap-2 justify-between">
          <CustomButton text={t("accept")} theme="light" onClick={onAcceptAll} className="text-xs!" />
          <CustomButton text={t("manage.save")} theme="dark" onClick={handleSave} />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface RowProps {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  badge?: string;
  onChange?: (val: boolean) => void;
}

function CookieRow({ title, description, checked, disabled, badge, onChange }: RowProps) {
  return (
    <div className="flex items-start justify-between gap-4 px-5 py-4">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
        {disabled && badge && (
          <span className="inline-block mt-1 text-xs text-primary font-medium">{badge}</span>
        )}
      </div>

      {/* Toggle switch built with Tailwind — no MUI dependency */}
      <button
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange?.(!checked)}
        className={cn(
          "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          checked ? "bg-primary" : "bg-muted",
          disabled && "cursor-not-allowed opacity-50",
        )}
      >
        <span
          className={cn(
            "pointer-events-none inline-block h-4 w-4 rounded-full bg-card shadow-lg ring-0 transition-transform duration-200",
            checked ? "translate-x-4" : "translate-x-0",
          )}
        />
      </button>
    </div>
  );
}
